import { ExecutionEngine, ExecutionEngineOptions } from '../engine/ExecutionEngine'
import { Parser } from '../parser/parser'
import { Variable } from '../types'
import { ExecutionLogger } from '../services/ExecutionLogger'

export interface ExecutionControllerOptions {
  onVariableUpdate: (variables: Map<string, Variable>) => void
  onLineHighlight: (line: number) => void
  onLog: (message: string, type?: 'info' | 'error' | 'success') => void
  onStateChange: (state: ExecutionState) => void
  onError?: (errors: Array<{ line: number; message: string }>) => void
  onLoopStackUpdate?: (loopStack: any[]) => void
}

export type ExecutionState = 'idle' | 'running' | 'paused' | 'completed' | 'error'

export class ExecutionController {
  private engine: ExecutionEngine | null = null
  private currentCode: string = ''
  private state: ExecutionState = 'idle'
  private options: ExecutionControllerOptions
  
  constructor(options: ExecutionControllerOptions) {
    this.options = options
  }
  
  /**
   * コードを設定
   */
  setCode(code: string): void {
    if (this.currentCode !== code) {
      this.currentCode = code
      this.reset()
    }
  }
  
  /**
   * 実行を開始
   */
  run(speed: number): void {
    if (this.state === 'running') return
    
    // 実行前に必ずリセット
    this.reset()
    if (!this.initializeEngine()) return
    
    // 実行記録
    const logger = ExecutionLogger.getInstance()
    const sampleId = this.getSampleIdFromUrl()
    logger.logExecution(this.currentCode, 'normal', sampleId)
    
    this.setState('running')
    this.engine!.run(speed)
  }
  
  /**
   * 一時停止
   */
  pause(): void {
    if (this.state !== 'running') return
    
    if (this.engine) {
      this.engine.pause()
      this.setState('paused')
    }
  }
  
  /**
   * ステップ実行
   */
  step(): void {
    // 実行中の場合は何もしない
    if (this.state === 'running') return
    
    // 初回ステップ実行時はリセットして記録
    if (this.state === 'idle' || this.state === 'completed') {
      this.reset()
      if (!this.initializeEngine()) return
      
      // ステップ実行記録
      const logger = ExecutionLogger.getInstance()
      const sampleId = this.getSampleIdFromUrl()
      logger.logExecution(this.currentCode, 'step', sampleId)
    }
    
    // エンジンが初期化されていない場合は初期化
    if (!this.engine) {
      if (!this.initializeEngine()) return
    }
    
    // 一時停止状態から再開
    if (this.state === 'paused') {
      this.setState('paused')  // ステップ実行中も一時停止状態を維持
    }
    
    const hasMore = this.engine!.step()
    if (!hasMore) {
      this.setState('completed')
      this.options.onLog('実行完了', 'success')
    }
  }
  
  /**
   * リセット
   */
  reset(): void {
    if (this.engine) {
      this.engine.reset()
      this.engine = null
    }
    
    this.setState('idle')
    this.options.onVariableUpdate(new Map())
    this.options.onLineHighlight(0)
    this.options.onLog('リセットしました', 'info')
    
    // ループスタックもクリア
    if (this.options.onLoopStackUpdate) {
      this.options.onLoopStackUpdate([])
    }
  }
  
  /**
   * 実行速度を変更
   */
  setSpeed(speed: number): void {
    if (this.engine && this.state === 'running') {
      // 一旦停止して再開
      this.engine.pause()
      this.engine.run(speed)
    }
  }
  
  /**
   * 現在の状態を取得
   */
  getState(): ExecutionState {
    return this.state
  }
  
  /**
   * エンジンが初期化されているか
   */
  isInitialized(): boolean {
    return this.engine !== null
  }
  
  /**
   * 実行ステップ数を取得
   */
  getStepCount(): number {
    return this.engine ? this.engine.getStepCount() : 0
  }
  
  private initializeEngine(): boolean {
    try {
      // パース
      const parser = new Parser(this.currentCode)
      const ast = parser.parse()
      const errors = parser.getErrors()
      
      if (errors.length > 0) {
        this.options.onLog('パースエラー:', 'error')
        const errorDetails = errors.map(error => ({
          line: error.line,
          message: error.message,
        }))
        
        errors.forEach(error => {
          this.options.onLog(`- ${error.message} at ${error.line}:${error.column}`, 'error')
        })
        
        if (this.options.onError) {
          this.options.onError(errorDetails)
        }
        
        this.setState('error')
        return false
      }
      
      // エンジン作成
      const engineOptions: ExecutionEngineOptions = {
        maxSteps: 10000,
        onStep: (step) => {
          this.options.onLineHighlight(step.lineNumber)
          this.options.onVariableUpdate(step.afterState.variables)
          this.options.onLog(`行 ${step.lineNumber}: ${step.instruction}`, 'info')
          
          // ループ情報をログ
          if (step.afterState.loopStack.length > 0) {
            const loop = step.afterState.loopStack[step.afterState.loopStack.length - 1]
            this.options.onLog(`  └─ ${loop.type}ループ: イテレーション ${loop.iteration}`, 'info')
          }
          
          // ループスタックを更新
          if (this.options.onLoopStackUpdate) {
            this.options.onLoopStackUpdate(step.afterState.loopStack)
          }
        },
        onError: (error) => {
          this.options.onLog(`実行エラー: ${error.message}`, 'error')
          this.setState('error')
          
          // 実行時エラーをエラー表示システムに送信
          if (this.options.onError) {
            this.options.onError([{
              line: this.engine?.getState().currentLine || 1,
              message: error.message
            }])
          }
        },
        onComplete: () => {
          // 実行完了時に最終的な変数状態を更新
          if (this.engine) {
            const finalState = this.engine.getState()
            this.options.onVariableUpdate(finalState.variables)
          }
          
          this.setState('completed')
          this.options.onLog('実行完了', 'success')
        },
        onLoopStackUpdate: (loopStack) => {
          if (this.options.onLoopStackUpdate) {
            this.options.onLoopStackUpdate(loopStack)
          }
        },
      }
      
      this.engine = new ExecutionEngine(ast, engineOptions)
      this.options.onLog('実行準備完了', 'success')
      return true
      
    } catch (error) {
      this.options.onLog(`エラー: ${error}`, 'error')
      this.setState('error')
      return false
    }
  }
  
  private setState(state: ExecutionState): void {
    this.state = state
    this.options.onStateChange(state)
    
    // 完了時の処理（エンジンは保持する）
    if (state === 'completed') {
      // エンジンはリセット時まで保持
    }
  }
  
  /**
   * URLからサンプルIDを取得
   */
  private getSampleIdFromUrl(): string | undefined {
    const path = window.location.pathname
    const match = path.match(/\/sample\/([^/]+)/)
    return match ? match[1] : undefined
  }
}